import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SchoolInput } from "./SchoolInput";
import { toast } from "sonner";
import { User, Camera, Link2, CircleCheck, Eye, Save, Send, GraduationCap, MapPin, Lock, Pencil, ImagePlus, Video, Play, Trash2, Mail, Share2, ShieldCheck, ShieldAlert, Clock, FileText, Info } from "lucide-react";
import { IMAGES } from "../data/content";
import { CampaignShareBar } from "./CampaignShareBar";
import { EmSwash } from "./EmSwash";
import { parseEmail } from "../lib/emailTrust";
import { getPolicy, subscribePolicy, evaluateGoal } from "../lib/platformPolicy";

const initial = {
  fullName: "", email: "", school: "", program: "", headline: "",
  gradYear: "", phone: "", location: "", bioHeadline: "", bio: "",
  goal: "", needType: "", deadline: "", funding: "", videoLink: "",
};

const Section = ({ title, subtitle, accent = "bg-tangerine", children }) => (
  <div className="relative overflow-hidden rounded-lg border border-border bg-bone">
    <span className={`absolute inset-y-0 left-0 w-1.5 ${accent}`} />
    <div className="p-6 pl-8 lg:p-9 lg:pl-11">
      <h3 className="font-serif text-3xl leading-tight text-ink">{title}</h3>
      <p className="mt-1 text-[13px] text-olive">{subtitle}</p>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, counter, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-[13px] font-medium text-ink">{label}</Label>
      {counter != null && <span className="text-[13px] text-olive">{counter}</span>}
    </div>
    {children}
    {hint && <p className="text-[13px] italic text-olive/80">{hint}</p>}
  </div>
);

const Preview = ({ data, visibility, photos = [], video, onBack, onSubmit }) => {
  const meta = [data.school, data.program].filter(Boolean).join(" · ");
  const headline = data.bioHeadline || data.headline;
  const goalNum = Number(data.goal) || 0;
  return (
    <>
      <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink">
        <Eye className="h-4 w-4" strokeWidth={1.75} /> Donor preview
      </div>

      <div data-testid="preview-panel" className="overflow-hidden rounded-lg border border-border bg-bone">
        <div className="relative bg-forest px-6 py-8 text-sand lg:px-9">
          <span className={`absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold ${visibility === "public" ? "bg-grass/25 text-sand" : "bg-sand/10 text-sand/80"}`}>
            {visibility === "public" ? <CircleCheck className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {visibility === "public" ? "Public" : "Private"}
          </span>
          <div className="flex items-center gap-5">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-cobalt text-sand">
              <User className="h-9 w-9" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif text-3xl leading-tight text-sand">{data.fullName || "Your name"}</h3>
              <p className="mt-1 text-[13px] text-sand/70">
                {meta || "School · Program"}{data.gradYear ? `, Class of '${String(data.gradYear).slice(-2)}` : ""}
              </p>
              {data.location && (
                <p className="mt-1 flex items-center gap-1 text-[13px] text-sand/60"><MapPin className="h-3 w-3" />{data.location}</p>
              )}
            </div>
          </div>
          {headline && <p className="mt-5 font-serif text-3xl italic text-yellow">“{headline}”</p>}
        </div>

        <div className="space-y-7 p-6 lg:p-9">
          <div>
            <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink">About</span>
            <p className="mt-3 text-base leading-relaxed text-olive">{data.bio || "Your bio will appear here once you add it."}</p>
          </div>
          <div>
            <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink">Funding need</span>
            <p className="mt-3 text-base leading-relaxed text-olive">{data.funding || "Your funding request will appear here once you add it."}</p>
          </div>

          {(video || data.videoLink || photos.length > 0) && (
            <div data-testid="preview-media">
              <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink">Media</span>
              {video && (
                <video controls src={video.url} className="mt-3 aspect-video w-full overflow-hidden rounded-md border border-border bg-ink object-cover" />
              )}
              {!video && data.videoLink && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-secondary/50 px-4 py-3 text-[13px] text-olive">
                  <Play className="h-4 w-4 text-tangerine" /> Video pitch linked
                </div>
              )}
              {photos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {photos.map((p) => (
                    <div key={p.id} className="aspect-square overflow-hidden rounded-md border border-border">
                      <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full w-0 rounded-full bg-tangerine" />
            </div>
            <div className="mt-2 flex items-center justify-between text-[13px]">
              <span className="font-semibold text-ink">$0 raised</span>
              <span className="text-olive">{goalNum ? `Goal ${"$" + goalNum.toLocaleString()}` : "Goal set after verification"}</span>
            </div>
          </div>
          <button disabled className="w-full cursor-not-allowed rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand opacity-50">
            Donate now
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 pt-2 sm:flex-row">
        <button
          data-testid="preview-back-btn"
          onClick={onBack}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-ink px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
        >
          <Pencil className="h-4 w-4" strokeWidth={1.75} /> Back to editing
        </button>
        <button
          data-testid="preview-submit-btn"
          onClick={onSubmit}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-yellow px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-yellow-dark"
        >
          <Send className="h-4 w-4" strokeWidth={1.75} /> Submit profile
        </button>
      </div>
    </>
  );
};

export const CreateCampaign = ({ open, onOpenChange }) => {
  const [data, setData] = useState(initial);
  const [visibility, setVisibility] = useState("public");
  const [eligibilityCert, setEligibilityCert] = useState(false);
  const [consent, setConsent] = useState(false);
  const [studentCert, setStudentCert] = useState(false);
  const [preview, setPreview] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [docs, setDocs] = useState([]);
  // Board-approved policy — grant cap, duration, docs threshold. Read live
  // so an admin edit in AdminSettings takes effect on the next render.
  const [policy, setPolicy] = useState(getPolicy);
  useEffect(() => subscribePolicy(setPolicy), []);
  const goalEval = evaluateGoal(data.goal);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((p) => [...p, ...files.map((f) => ({ id: `${Date.now()}-${Math.random()}`, url: URL.createObjectURL(f), name: f.name }))]);
    e.target.value = "";
  };
  const removePhoto = (id) => setPhotos((p) => {
    const t = p.find((x) => x.id === id);
    if (t) URL.revokeObjectURL(t.url);
    return p.filter((x) => x.id !== id);
  });
  const addVideo = (e) => {
    const f = e.target.files?.[0];
    if (f) setVideo((v) => { if (v) URL.revokeObjectURL(v.url); return { url: URL.createObjectURL(f), name: f.name }; });
    e.target.value = "";
  };
  const removeVideo = () => setVideo((v) => { if (v) URL.revokeObjectURL(v.url); return null; });

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      if (video) URL.revokeObjectURL(video.url);
      docs.forEach((d) => URL.revokeObjectURL(d.url));
      setData(initial); setVisibility("public"); setEligibilityCert(false); setConsent(false); setStudentCert(false); setPreview(false); setSubmitted(false); setPhotos([]); setVideo(null); setDocs([]);
    }, 300);
  };
  const notReady = (msg) => toast("Prototype", { description: msg });

  const onDraft = () => toast.success("Draft saved", { description: "Your progress is kept locally in this prototype." });
  // TODO(backend): Replace `setSubmitted(true)` with:
  //   1. Upload photos[] and video via multipart/form-data to object storage.
  //   2. POST /api/campaigns  { ...data, mediaUrls, visibility, consent }
  //   3. On 201, redirect to `/campaign/:id` and preserve share-flow state.
  //   Bypass all of the above when `isDemoMode()` returns true.
  const onSubmit = () => {
    if (!eligibilityCert) { toast.error("First, confirm you're the enrolled student creating this campaign."); return; }
    if (!consent) { toast.error("Please agree to enrollment verification before you submit."); return; }
    if (!studentCert) { toast.error("One more thing: please review and check the student certification."); return; }
    if (goalEval.exceedsCap) {
      toast.error(`During the ${goalEval.activePhase} phase, campaigns can request up to $${goalEval.cap}.`, {
        description: "Lower your goal amount to submit.",
      });
      return;
    }
    if (goalEval.requiresDocs && docs.length === 0) {
      toast.error("Please upload supporting documentation.", {
        description: `Campaigns requesting more than $${goalEval.threshold} require documentation.`,
      });
      return;
    }
    setSubmitted(true);
  };

  // Live count of ticked attestations (used by the wizard's Attestations
  // group header so the student sees "N of 3 required" → "All 3 complete"
  // at a glance without hunting for which box is still open).
  const attestationsChecked = [eligibilityCert, consent, studentCert].filter(Boolean).length;
  const attestationsComplete = attestationsChecked === 3;

  // Success celebration — mirrors DonateDialog: yellow header + color logo +
  // pulsing tangerine ring, then a sand-body "what happens next" panel.
  const firstName = (data.fullName || "").trim().split(/\s+/)[0];
  const goalNumSuccess = Number(data.goal) || 0;
  const goalDisplay = goalNumSuccess ? `$${goalNumSuccess.toLocaleString()}` : "your goal";
  // Pre-populated share text using the student's wizard entries.
  const launchShareText = firstName
    ? `${firstName} just launched a My Rich Aunt campaign${
        data.school ? ` at ${data.school}` : ""
      }. Raising ${goalDisplay}. Every gift is tax-deductible. Help ${firstName} get started.`
    : `I just launched my My Rich Aunt campaign. Raising ${goalDisplay}. Every gift is tax-deductible. Help me get started.`;
  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
        <DialogContent
          data-testid="create-campaign-success"
          className="max-w-2xl overflow-hidden border-border bg-sand p-0 gap-0 sm:rounded-lg"
        >
          {/* Yellow celebration header */}
          <div className="relative overflow-hidden bg-yellow px-8 py-9 text-center text-ink">
            <span className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-tangerine/40 blur-3xl" />
            <span className="pointer-events-none absolute -left-10 -bottom-10 h-36 w-36 rounded-full bg-blossom/50 blur-3xl" />
            <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
              <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full bg-tangerine/60"
                animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity }}
              />
              <img
                src={IMAGES.logoColor}
                alt="My Rich Aunt"
                className="relative h-20 w-auto mix-blend-multiply"
                data-testid="create-success-logo"
              />
            </div>
            <DialogTitle className="relative mt-4 font-serif text-3xl font-normal text-ink">
              Welcome to the family{firstName ? `, ${firstName}` : ""}!
            </DialogTitle>
            <DialogDescription className="relative mt-2 text-[13px] text-ink/75">
              Your campaign is queued for enrollment verification. We&rsquo;ll email you the moment your page is live.
            </DialogDescription>
          </div>

          {/* Sand body — what happens next */}
          <div className="bg-sand px-8 py-8">
            {/* Explicit status banner — visible from the moment the wizard closes */}
            <div
              className="mb-6 flex items-start gap-3 rounded-md border border-tangerine/30 bg-tangerine/[0.06] p-4"
              data-testid="pending-verification-banner"
            >
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-tangerine" strokeWidth={2} />
              <div>
                <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
                  Pending verification
                </p>
                <p className="mt-1 text-[13px] font-medium text-ink">
                  Your campaign is under review.
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-ink/70">
                  {parseEmail(data.email).isEdu
                    ? "We spotted your .edu address. Thanks, that's a helpful trust signal, and we'll still confirm enrollment through the National Student Clearinghouse before your page goes live."
                    : "We'll confirm your current enrollment through the National Student Clearinghouse. A .edu email helps speed this up if you have one at your school."}
                </p>
              </div>
            </div>

            <p className="text-[13px] font-medium uppercase tracking-[0.2em] text-olive">What happens next</p>
            <ul className="mt-4 space-y-3.5">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cobalt/12">
                  <CircleCheck className="h-3.5 w-3.5 text-cobalt" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-[13px] font-semibold text-ink">Enrollment verification</p>
                  <p className="text-[13px] text-olive">We confirm you&rsquo;re actively enrolled.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tangerine/15">
                  <Mail className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-[13px] font-semibold text-ink">Your page goes live</p>
                  <p className="text-[13px] text-olive">You&rsquo;ll get an email with your unique campaign link.</p>
                </div>
              </li>
            </ul>

            {/* Get-ready-to-share nudge — pre-populated with the student's
                own campaign entries so they feel the shape of the moment
                before their page is actually live. */}
            <div
              data-testid="create-success-share"
              className="mt-8 rounded-md border border-grass/25 bg-grass/8 p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-grass/15">
                  <Share2 className="h-3.5 w-3.5 text-grass" strokeWidth={2} />
                </span>
                <p className="font-serif text-3xl leading-snug text-ink">
                  Get ready to <EmSwash color="text-grass">spread the word.</EmSwash>
                </p>
              </div>
              <p className="mt-3 text-base leading-relaxed text-olive">
                The sooner your extended family, roommates, and group chats know
                the goal, the faster you get funded. Pick a channel &mdash; we&rsquo;ve
                already written the message.
              </p>
              <CampaignShareBar
                shareText={launchShareText}
                label="Share your launch"
              />
            </div>

            <button
              onClick={close}
              data-testid="create-success-done"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        data-testid="create-campaign-dialog"
        className="max-w-4xl max-h-[92vh] overflow-y-auto border-border bg-sand p-0 gap-0 sm:rounded-lg"
      >
        {/* Header */}
        <div className="border-b border-border bg-sand px-6 pt-8 pb-6 lg:px-10">
          <DialogTitle className="font-serif text-3xl lg:text-[3.5rem] font-normal leading-none tracking-tight text-ink">Create Your Profile & Campaign</DialogTitle>
          <DialogDescription className="mt-2 text-base text-olive">Set up your profile. It's what donors see when they support your campaign.</DialogDescription>
        </div>

        {/* Body */}
        <div className="space-y-6 px-6 py-8 lg:px-10">
          {preview ? (
            <Preview data={data} visibility={visibility} photos={photos} video={video} onBack={() => setPreview(false)} onSubmit={onSubmit} />
          ) : (
          <>
          {/* Personal Details */}
          <Section accent="bg-cobalt" title="Personal details" subtitle="Tell us a bit about you and your school.">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Full name"><Input data-testid="field-fullname" value={data.fullName} onChange={set("fullName")} placeholder="Alice Smith" /></Field>
              <Field label="Email address" hint="A .edu email speeds up verification.">
                <div className="relative">
                  <Input
                    data-testid="field-email"
                    type="email"
                    value={data.email}
                    onChange={set("email")}
                    placeholder="you@your-school.edu"
                    className="pr-24"
                  />
                  {data.email && parseEmail(data.email).isEdu && (
                    <span
                      className="absolute inset-y-1 right-1 inline-flex items-center gap-1 rounded-full bg-grass/15 px-2.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-grass ring-1 ring-inset ring-grass/30"
                      data-testid="edu-email-badge"
                    >
                      <ShieldCheck className="h-3 w-3" strokeWidth={2} /> .edu
                    </span>
                  )}
                </div>
              </Field>
              <Field label="School"><SchoolInput testid="field-school" value={data.school} onChange={(v) => setData((d) => ({ ...d, school: v }))} placeholder="Start typing your school name" /></Field>
              <Field label="Program / Major"><Input data-testid="field-program" value={data.program} onChange={set("program")} placeholder="Computer Science" /></Field>
              <Field label="Profile headline"><Input data-testid="field-headline" value={data.headline} onChange={set("headline")} placeholder="A first-gen student studying data science" /></Field>
              <Field label="Graduation year"><Input data-testid="field-gradyear" value={data.gradYear} onChange={set("gradYear")} placeholder="2026" /></Field>
              <Field label="Phone number"><Input data-testid="field-phone" value={data.phone} onChange={set("phone")} placeholder="+1 (555) 123-4567" /></Field>
              <Field label="Location"><Input data-testid="field-location" value={data.location} onChange={set("location")} placeholder="Boston, MA, US" /></Field>
            </div>
          </Section>

          {/* Profile Photo & Bio */}
          <Section accent="bg-blossom" title="Profile photo & bio" subtitle="Add a photo, headline, and a short bio donors will read.">
            <div className="flex flex-col gap-7 sm:flex-row">
              <div className="flex shrink-0 flex-col items-center gap-3">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-cobalt text-sand">
                  <User className="h-10 w-10" strokeWidth={1.5} />
                </div>
                <button
                  data-testid="change-photo"
                  onClick={() => notReady("Photo uploads arrive with the full launch.")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
                >
                  <Camera className="h-3.5 w-3.5" strokeWidth={1.75} /> Change photo
                </button>
              </div>
              <div className="flex-1 space-y-5">
                <Field label="Headline"><Input data-testid="field-bio-headline" value={data.bioHeadline} onChange={set("bioHeadline")} placeholder="Aspiring software engineer & community volunteer" /></Field>
                <Field label="Biography" counter={`${data.bio.length}/500`}>
                  <Textarea data-testid="field-bio" maxLength={500} rows={4} value={data.bio} onChange={set("bio")} placeholder="Tell us about yourself..." />
                </Field>
              </div>
            </div>
          </Section>

          {/* Funding Need */}
          <Section accent="bg-yellow" title="Funding need" subtitle="Tell us what you need and how much you're raising.">
            {/* Policy summary strip — the numbers a student needs to see up front */}
            <div
              data-testid="policy-summary-strip"
              className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-tangerine/25 bg-tangerine/[0.05] px-4 py-3 text-[13px] text-ink"
            >
              <div className="flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                <span className="font-semibold uppercase tracking-[0.16em] text-tangerine">
                  {policy.activePhase === "beta" ? "Beta launch" : "Pilot launch"}
                </span>
              </div>
              <span>
                Grant cap: <strong className="text-ink">{goalEval.cap != null ? `$${goalEval.cap}` : "TBD"}</strong>
              </span>
              <span>
                Campaign length: <strong className="text-ink">{policy.campaignDurationDays} days</strong>
              </span>
              <span>
                Disbursement: <strong className="text-ink">weekly, equal installments</strong>
              </span>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field
                label="Goal amount (USD)"
                hint={
                  goalEval.exceedsCap
                    ? `Above the current cap ($${goalEval.cap}). Please lower to submit.`
                    : goalEval.cap != null
                      ? `Maximum during ${goalEval.activePhase}: $${goalEval.cap}.`
                      : undefined
                }
              >
                <Input
                  data-testid="field-goal"
                  type="number"
                  value={data.goal}
                  onChange={set("goal")}
                  placeholder={goalEval.cap != null ? String(Math.min(goalEval.cap, 1000)) : "1000"}
                  className={goalEval.exceedsCap ? "border-scarlet ring-scarlet/30" : ""}
                />
              </Field>
              <Field label="Campaign Category">
                <Select value={data.needType} onValueChange={set("needType")}>
                  <SelectTrigger data-testid="field-needtype"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                  <SelectContent>
                    {["Trip home", "Study abroad", "Move-in essentials", "Textbooks & tools", "First Gen", "Other"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Optional deadline">
                <Input data-testid="field-deadline" type="date" value={data.deadline} onChange={set("deadline")} />
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Funding need description" counter={`${data.funding.length}/1000`}>
                <Textarea data-testid="field-funding" maxLength={1000} rows={5} value={data.funding} onChange={set("funding")} placeholder="Tell us what you'll use the money for..." />
              </Field>
            </div>

            {/* Docs upload — appears only when policy requires it (pilot phase, goal above threshold) */}
            {goalEval.requiresDocs && (
              <div
                data-testid="docs-upload-zone"
                className="mt-6 rounded-md border border-cobalt/30 bg-cobalt/[0.04] p-5"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-cobalt" strokeWidth={1.75} />
                  <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-cobalt">
                    Supporting documentation required
                  </span>
                </div>
                <p className="mt-2 text-base leading-relaxed text-ink">
                  Campaigns requesting more than <strong>${goalEval.threshold}</strong> require documentation of your expenses. Upload receipts, quotes, or itemized budgets that back up your goal amount.
                </p>
                <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md border border-ink/15 bg-bone px-3 py-2 text-[13px] font-medium text-ink hover:bg-sand">
                  <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                  Add document
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="sr-only"
                    data-testid="docs-upload-input"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setDocs((prev) => [
                        ...prev,
                        ...files.map((f) => ({
                          id: `${Date.now()}-${Math.random()}`,
                          url: URL.createObjectURL(f),
                          name: f.name,
                        })),
                      ]);
                      e.target.value = "";
                    }}
                  />
                </label>
                {docs.length > 0 && (
                  <ul className="mt-3 space-y-1 text-[13px] text-ink" data-testid="docs-upload-list">
                    {docs.map((d) => (
                      <li key={d.id} className="flex items-center gap-2">
                        <FileText className="h-3 w-3 text-cobalt" strokeWidth={2} />
                        {d.name}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-[13px] italic text-ink/60">
                  Docs stay private to My Rich Aunt administrators. Donors don't see them.
                </p>
              </div>
            )}
            <button
              data-testid="connect-stripe"
              onClick={() => notReady("Payout setup with Stripe becomes available at launch.")}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-cobalt px-5 py-2.5 text-base font-semibold text-sand transition-opacity hover:opacity-90"
            >
              <Link2 className="h-4 w-4" strokeWidth={1.75} /> Connect Stripe account
            </button>
          </Section>

          {/* Photos */}
          <Section accent="bg-tangerine" title="Photos" subtitle="Add photos that help donors get to know you.">
            <label
              data-testid="add-photos"
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/40 px-6 py-8 text-center transition-colors hover:border-scarlet/50 hover:bg-secondary/70"
            >
              <ImagePlus className="h-7 w-7 text-scarlet" strokeWidth={1.5} />
              <span className="text-[13px] font-semibold text-ink">Add photos</span>
              <span className="text-[13px] text-olive">PNG or JPG. You can select multiple.</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
            </label>
            {photos.length > 0 && (
              <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {photos.map((p) => (
                  <div key={p.id} data-testid="photo-thumb" className="group relative aspect-square overflow-hidden rounded-md border border-border">
                    <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      aria-label="Remove photo"
                      onClick={() => removePhoto(p.id)}
                      className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-sand opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Video Pitch */}
          <Section accent="bg-scarlet" title="Video pitch (optional)" subtitle="Record a short intro if you'd like. Video helps donors connect.">
            <Field label="Video link or embed code">
              <Input data-testid="field-videolink" value={data.videoLink} onChange={set("videoLink")} placeholder="Paste a YouTube, Vimeo, or video link" />
            </Field>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[13px] uppercase tracking-[0.15em] text-olive">or upload</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <label
              data-testid="add-video"
              className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/40 px-6 py-8 text-center transition-colors hover:border-tangerine/50 hover:bg-secondary/70"
            >
              <Video className="h-7 w-7 text-tangerine" strokeWidth={1.5} />
              <span className="text-[13px] font-semibold text-ink">Upload a video</span>
              <span className="text-[13px] text-olive">MP4 or MOV, up to a couple of minutes.</span>
              <input type="file" accept="video/*" className="hidden" onChange={addVideo} />
            </label>
            {video && (
              <div data-testid="video-preview" className="mt-5 overflow-hidden rounded-md border border-border">
                <video controls src={video.url} className="aspect-video w-full bg-ink object-cover" />
                <div className="flex items-center justify-between bg-bone px-4 py-2.5">
                  <span className="truncate text-[13px] text-olive">{video.name}</span>
                  <button type="button" onClick={removeVideo} className="inline-flex items-center gap-1 text-[13px] font-semibold text-scarlet hover:opacity-80">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              </div>
            )}
          </Section>

          {/* Profile Visibility */}
          <Section accent="bg-grass" title="Profile visibility" subtitle="Choose whether your profile is visible to potential donors.">
            <RadioGroup value={visibility} onValueChange={setVisibility} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { v: "public", label: "Public profile", desc: "Discoverable by donors" },
                { v: "private", label: "Private profile", desc: "Only shared via direct link" },
              ].map((o) => (
                <label
                  key={o.v}
                  data-testid={`visibility-${o.v}`}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                    visibility === o.v ? "border-cobalt bg-cobalt/[0.04]" : "border-border bg-bone hover:border-cobalt/40"
                  }`}
                >
                  <RadioGroupItem value={o.v} className="mt-0.5" />
                  <span>
                    <span className="block text-[13px] font-semibold text-ink">{o.label}</span>
                    <span className="block text-[13px] text-olive">{o.desc}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>

            {visibility === "public" && (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-yellow/50 bg-yellow/[0.14] p-4">
                <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-tangerine" strokeWidth={1.75} />
                <p className="text-base leading-relaxed text-ink">
                  Great choice. Public profiles raise more — <span className="font-semibold text-ink underline decoration-tangerine decoration-2 underline-offset-2">for you first, and for My Rich Aunt with you</span> — because donors want to hear from students like you, not from us.
                </p>
              </div>
            )}
          </Section>

          {/* Consent + student certifications — all three required to submit.
              Order: (1) Campaign eligibility attestation (who is creating
              this), (2) enrollment verification consent, (3) grant-usage
              certification. The eligibility attestation mirrors the Terms
              "Campaign Eligibility" section and enforces that only the
              beneficiary student may submit a campaign.

              Header row shows a live "N of 3 required" counter that flips
              to a grass "All 3 complete" chip when the student has ticked
              every box — so the required-to-submit surface is legible at
              a glance. */}
          <div
            data-testid="attestations-group"
            className="space-y-4 rounded-lg border border-border bg-bone/40 p-5"
          >
            <div className="flex items-baseline justify-between gap-4 border-b border-border/70 pb-3">
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-ink">
                Attestations
              </span>
              {attestationsComplete ? (
                <span
                  data-testid="attestations-count"
                  className="inline-flex items-center gap-1.5 rounded-full bg-grass/15 px-2.5 py-0.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-grass ring-1 ring-inset ring-grass/30"
                >
                  <CircleCheck className="h-3 w-3" strokeWidth={2} /> All 3 complete
                </span>
              ) : (
                <span
                  data-testid="attestations-count"
                  className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive"
                >
                  {attestationsChecked} of 3 required
                </span>
              )}
            </div>

            <label
              data-testid="eligibility-cert-row"
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-scarlet/40 bg-scarlet/[0.04] p-5"
            >
              <Checkbox
                data-testid="eligibility-cert-checkbox"
                checked={eligibilityCert}
                onCheckedChange={(v) => setEligibilityCert(Boolean(v))}
                className="mt-0.5"
              />
              <span>
                <span className="block text-[13px] font-semibold text-ink">Campaign eligibility attestation</span>
                <span className="mt-1 block text-[13px] leading-relaxed text-olive">
                  I am the enrolled student named above and the intended beneficiary of this campaign. I am personally creating and submitting this campaign. No parent, sibling, guardian, spouse, friend, classmate, teacher, employer, or other third party has created this campaign on my behalf. I understand that any account identified as having been created by someone other than the beneficiary student will be suspended immediately, and funds raised from donors who stated a non-binding preference for contributing toward the suspended campaign will be used to support other eligible campaigns.
                </span>
                <a
                  href="/terms#campaign-eligibility"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-[13px] font-semibold uppercase tracking-[0.14em] text-scarlet underline underline-offset-2 hover:text-ink"
                  data-testid="eligibility-cert-terms-link"
                >
                  Read the full Campaign Eligibility clause
                </a>
              </span>
            </label>

            <label
              data-testid="consent-row"
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-cobalt/40 bg-cobalt/[0.04] p-5"
            >
              <Checkbox data-testid="consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(Boolean(v))} className="mt-0.5" />
              <span>
                <span className="block text-[13px] font-semibold text-ink">I consent to enrollment verification</span>
                <span className="mt-1 block text-[13px] text-olive">
                  I authorize My Rich Aunt to verify my current enrollment status through the National Student Clearinghouse.
                </span>
              </span>
            </label>

            <label
              data-testid="student-cert-row"
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-tangerine/40 bg-tangerine/[0.04] p-5"
            >
              <Checkbox
                data-testid="student-cert-checkbox"
                checked={studentCert}
                onCheckedChange={(v) => setStudentCert(Boolean(v))}
                className="mt-0.5"
              />
              <span>
                <span className="block text-[13px] font-semibold text-ink">Student certification</span>
                <span className="mt-1 block text-[13px] leading-relaxed text-olive">
                  I am at least 18 years of age and currently enrolled as a student at an accredited college or university in the United States. I agree that any funds awarded to me through My Rich Aunt's supplemental educational grant program will be used exclusively to support education-related and student living costs while enrolled, which are not already covered by my financial aid package. I understand that My Rich Aunt does not require me to submit receipts for grants of $500 or less but relies on this legally binding certification for federal tax compliance.
                </span>
                <a
                  href="/faq?group=students&open=expense-guidelines"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-[13px] font-semibold uppercase tracking-[0.14em] text-tangerine underline underline-offset-2 hover:text-ink"
                  data-testid="student-cert-expenses-link"
                >
                  See the Expense Guidelines
                </a>
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              data-testid="profile-preview-btn"
              onClick={() => setPreview(true)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-ink px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
            >
              <Eye className="h-4 w-4" strokeWidth={1.75} /> Profile preview
            </button>
            <button
              data-testid="submit-draft-btn"
              onClick={onDraft}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-tangerine px-6 py-3.5 text-base font-semibold text-tangerine transition-colors hover:bg-tangerine hover:text-sand"
            >
              <Save className="h-4 w-4" strokeWidth={1.75} /> Save draft
            </button>
            <button
              data-testid="submit-profile-btn"
              onClick={onSubmit}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-yellow px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-yellow-dark"
            >
              <Send className="h-4 w-4" strokeWidth={1.75} /> Submit profile
            </button>
          </div>
          </>
          )}

          <div className="flex items-center justify-center gap-2 pb-1 text-[13px] text-olive">
            <GraduationCap className="h-4 w-4 text-tangerine" strokeWidth={1.5} />
            Prototype only. No information is stored and no charges are made.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
